/**
 * What one line of an order IS — on the wire, on the screen, and as an identity.
 *
 * The basket lives in Postgres now, so a line has three shapes and this file
 * owns the two that leave the server. `StoredLine` is the wire: slugs, never
 * ids. `OrderLine` is what the screen edits: the same thing with the pick put
 * back together, because a row is stored as a PICK and never as a resolved
 * reference — see `cart_item`'s docblock for why that direction is the only one
 * that can be reopened for editing.
 *
 * There is no zod here and there must not be. This module is imported by the
 * order screen, which used to reach `zod` through the basket store in the root
 * layout and pull 301 kB of it into the first load of every page on the site,
 * the holding page included. Validation belongs to the server action, which is where
 * an untrusted payload actually arrives.
 */
import type { CreatorPick } from '@/lib/creator'

/**
 * How long a brief may be, mirroring `BRIEF_MAX_CHARS` in `@repo/core`'s
 * checkout schema. Repeated for the same reason `MAX_RENTAL_DAYS` is repeated
 * in `lib/rental.ts`: `@repo/core` has no leaf export a client bundle can pull
 * in without dragging the database layer along. The textarea's `maxLength` and
 * the server's `.max()` have to agree, and this is the browser's copy.
 */
export const BRIEF_MAX_CHARS = 4000

/** The same, for the source link — mirrors `SOURCE_URL_MAX_CHARS` in core. */
export const SOURCE_URL_MAX_CHARS = 2000

/**
 * A catalogue REFERENCE — what the checkout sends. It names WHAT was ordered
 * (slugs), WHEN it is wanted and WHERE; it never carries a price. The server
 * resolves the current price from the database and recomputes every total, so
 * nothing a stale or tampered browser submits can change what is charged.
 *
 * It mirrors `CheckoutLineRef` field for field, so basket → order is a copy
 * rather than a translation — which is why a camera and a montage package
 * differ here only by which optional fields they fill in.
 */
export type CartLineRef = {
  offeringSlug: string
  /** Absent only for a line with no variant to price. */
  variantSlug?: string
  /** `YYYY-MM-DD` for whole days, `YYYY-MM-DDTHH:MM` for an hour of a room. */
  from?: string
  to?: string
  /** Billable days, for a date range. */
  days?: number
  /** Where the work happens, for an offering that covers cities. */
  citySlug?: string
  /** What the customer wants made — spec §6. Absent for anything unbriefed. */
  brief?: string
  /** Where the raw material is — spec §7. Absent for anything that needs none. */
  sourceUrl?: string
  /** The length band a film sits on — spec §8. Absent for anything else. */
  lengthBand?: number
  /** Declared minutes of raw footage — spec §8. Absent when none was asked for. */
  footageMinutes?: number
  /**
   * What the shoot is — the three answers `/pl/fotograf-operator` collected.
   *
   * INSIDE the identity, like the film's length band and for the same reason: a
   * three-person half-day with 15 photos and a ten-person full day with 50 are
   * different products at different prices. Merging them the way two identical
   * thumbnails merge would silently reprice one of them.
   */
  shootSession?: string
  shootScope?: string
  shootPhotos?: number
  /**
   * What is being recorded — the operator's one answer, inside the identity for
   * the same reason the shoot's three are: a podcast day and an event day are
   * different products, and merging them would reprice one of them.
   */
  recordingType?: string
  /**
   * The basket line this one is cut from — spec §8's "rolki z tego filmu" (G8).
   *
   * The ONE field here that does not mirror `CheckoutLineRef`, and it cannot:
   * the wire carries a POSITION in the payload, this carries a basket `lineId`.
   * They name the same relationship from two sides of a boundary that has no
   * shared vocabulary for it — the browser has ids and no positions, the
   * checkout has positions and refuses ids.
   *
   * It is here rather than beside the ref because it belongs to the line's
   * IDENTITY (see `cartLineId`): a reel cut from a film and a reel cut from
   * nothing are two different things to buy. `toCheckoutItems` never sets it —
   * it works from rows and does its own translation.
   */
  fromFilmLineId?: string
}

/**
 * One line as it crosses the wire. Slugs out, ids kept on the server.
 *
 * Declared here rather than beside the actions on purpose: the screen imports
 * this type, and importing it from a `'use server'` module would drag the whole
 * action file — and its database imports — into the client graph.
 */
export type StoredLine = {
  lineId: string
  offeringSlug: string
  variantSlug: string | null
  citySlug: string | null
  quantity: number
  fromDay: string | null
  toDay: string | null
  startTime: string | null
  brief: string | null
  sourceUrl: string | null
  /** The film slider's position — `null` for anything not priced by the minute. */
  lengthBandIndex: number | null
  /** Declared minutes of raw footage — spec §8. NULL when none was asked for. */
  footageMinutes: number | null
  /**
   * What the shoot is — the three answers `/pl/fotograf-operator` collected.
   *
   * All three or none; the database refuses anything between
   * (`cart_item_shoot_triple`). NULL for every line that is not a photography
   * day, which is almost all of them.
   */
  shootSession: string | null
  shootScope: string | null
  shootPhotos: number | null
  /** The `recording_type` slug an operator's day was booked for. NULL elsewhere. */
  recordingType: string | null
  /**
   * The basket line this one is cut from — spec §8's "rolki z tego filmu" (G8).
   *
   * A `lineId` and not a slug, because it names a POSITION IN THIS BASKET
   * rather than something in the catalogue: two films of the same level in one
   * order are two different answers, and a slug cannot tell them apart.
   */
  dependsOnLineId: string | null
  sortOrder: number
}

/**
 * One line as the screen holds it.
 *
 * `lineId` and the content signature are DIFFERENT THINGS and keeping them
 * apart is what makes the merged screen possible at all. `lineId` is minted
 * once by the database: it is React's key, the address an action patches and
 * the element focus returns to, and it survives every edit. The signature
 * (`cartLineId` below) changes whenever the configuration does, and answers
 * only one question — is this the same line as that one.
 */
export type OrderLine = {
  lineId: string
  offeringSlug: string
  pick: CreatorPick
  sortOrder: number
}

/** The wire shape, put back together into something the fields can edit. */
export function toOrderLine(row: StoredLine): OrderLine {
  return {
    lineId: row.lineId,
    offeringSlug: row.offeringSlug,
    sortOrder: row.sortOrder,
    pick: {
      variantSlug: row.variantSlug,
      qty: row.quantity,
      from: row.fromDay ?? '',
      to: row.toDay ?? '',
      time: row.startTime ?? '',
      citySlug: row.citySlug ?? '',
      brief: row.brief ?? '',
      sourceUrl: row.sourceUrl ?? '',
      lengthBand: row.lengthBandIndex,
      footageMinutes: row.footageMinutes === null ? '' : String(row.footageMinutes),
      fromFilmLineId: row.dependsOnLineId,
      shootSession: row.shootSession ?? '',
      shootScope: row.shootScope ?? '',
      shootPhotos: row.shootPhotos === null ? '' : String(row.shootPhotos),
      recordingType: row.recordingType ?? '',
    },
  }
}

/**
 * The pick, flattened to what the action's schema takes.
 *
 * Deliberately narrower than `OrderLine`: a line that does not exist yet has no
 * `lineId` and no `sortOrder`, and `addLineAction` needs exactly the same
 * payload as `patchLineAction`. One shape, both directions.
 */
export function toPick(line: { offeringSlug: string; pick: CreatorPick }) {
  return {
    offeringSlug: line.offeringSlug,
    variantSlug: line.pick.variantSlug,
    citySlug: line.pick.citySlug || null,
    qty: line.pick.qty,
    from: line.pick.from,
    to: line.pick.to,
    time: line.pick.time,
    brief: line.pick.brief,
    sourceUrl: line.pick.sourceUrl,
    lengthBand: line.pick.lengthBand,
    footageMinutes: line.pick.footageMinutes,
    fromFilmLineId: line.pick.fromFilmLineId,
    shootSession: line.pick.shootSession,
    shootScope: line.pick.shootScope,
    shootPhotos: line.pick.shootPhotos,
    recordingType: line.pick.recordingType,
  }
}

/**
 * The content signature: what makes two lines ONE line.
 *
 * Computed on the server for a stored line and here for a line still being
 * typed, from the same reference either way, so the browser and the unique
 * index on `(cart_id, line_key)` cannot disagree about what a duplicate is.
 */
export function cartLineId(ref: CartLineRef): string {
  // The whole window, not just its start. With the start alone, 24–26 sierpnia
  // and 24–28 sierpnia were ONE id: adding the longer hire found the shorter
  // one, bumped its quantity to 2, and billed twice the shorter price — while
  // still sending the server the shorter window. Two terms, one line, wrong
  // number, wrong dates.
  const term = ref.from ? `-${ref.from}${ref.to && ref.to !== ref.from ? `_${ref.to}` : ''}` : ''
  // Same reasoning one field over: the same shoot on the same day in two cities
  // is two jobs, and coverage is a field the customer picks.
  const city = ref.citySlug ? `-${ref.citySlug}` : ''
  // Spec §8's length band, and it had to be added: `refFrom` has always put the
  // band on the ref and this function has always ignored it, so a 20–30 minute
  // film and a 60–70 minute one signed identically. Adding the second one found
  // the first, merged into it and bumped its quantity — and since a film's
  // quantity is derived from its band at checkout, the longer film did not get
  // repriced, it simply DISAPPEARED. Two films ordered, one delivered.
  const band = ref.lengthBand === undefined ? '' : `-min${String(ref.lengthBand)}`
  // Same argument one field over: a reel cut from a film in this basket and a
  // reel cut from the client's own tape are different work at different prices
  // (one carries the footage surcharge, the other does not) and start at
  // different times. Merging them would silently drop one of the two.
  const film = ref.fromFilmLineId ? `-z-${ref.fromFilmLineId}` : ''
  // And the TAPE, which is the same argument a third time. Two reels of the
  // same level cut from two different videos signed identically: adding the
  // second found the first, merged into it and bumped its quantity — so both
  // editors got the SAME link, and the second reel's declared footage was
  // thrown away with it. That last part is money: the limit is 10 minutes per
  // reel, so a merged pair allows 20 while carrying the survivor's 5, and a
  // declaration of 200 minutes silently stopped costing the 380 zł net it owes.
  // The schema said as much all along — "two reels cut from different amounts
  // of tape are still two reels".
  //
  // Hashed rather than spelled out: a signature is also a database key, and a
  // link can be five hundred characters of query string. `tapeMark` is a plain
  // digest — same function on the browser and on the server, which is the only
  // property this needs.
  const tape = ref.sourceUrl ? `-t${tapeMark(ref.sourceUrl)}` : ''
  const footage =
    ref.footageMinutes === undefined || ref.footageMinutes === null
      ? ''
      : `-s${String(ref.footageMinutes)}`
  return `${ref.offeringSlug}:${ref.variantSlug ?? 'bez-wariantu'}${term}${city}${band}${film}${tape}${footage}`
    .toLowerCase()
    .replace(/\s+/g, '-')
}

/**
 * A short, stable mark for one source link — FNV-1a, in base 36.
 *
 * Not a security primitive and not trying to be: the only question it answers
 * is "is this the same tape as that one", and a collision costs a merge that
 * was already the old behaviour. Written out rather than imported because it
 * has to run identically in the browser and on the server, and `node:crypto`
 * does not exist in the first of those.
 */
function tapeMark(url: string): string {
  let hash = 0x81_1c_9d_c5
  for (let i = 0; i < url.length; i += 1) {
    hash ^= url.charCodeAt(i)
    hash = Math.imul(hash, 0x01_00_01_93)
  }
  return (hash >>> 0).toString(36)
}
